import { Card, Stack, Table, Button, TableBody, Typography, TableContainer } from '@mui/material';

import { useState, useEffect, useCallback } from 'react';
import { useBoolean } from 'src/hooks/use-boolean';

import { Pagination } from 'src/type/utils';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import { fParams, ParamsType } from 'src/utils/format-params';
import usePagination from 'src/hooks/use-pagination';
import Scrollbar from 'src/components/scrollbar';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableLoading from 'src/components/table/table-loading';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import { emptyRows } from 'src/components/table/utils';
import Iconify from 'src/components/iconify';
import { ProductType } from 'src/type/product';
import { ProductAmountConfigType } from 'src/type/product-amount-config';
import ProductAmountFormDialog from './product-amount-form-dialog';
import ProductAmountTableRow from './product-amount-table-row';

const tableHead = [
  { id: 'name', label: 'ชื่อหน่วย' },
  { id: 'amountBaht', label: 'จำนวน (บาท)' },
  { id: 'amountGram', label: 'จำนวน (กรัม)' },
  { id: 'action', width: 60 },
];

type Props = {
  product: ProductType;
};

export default function ProductAmountTable({ product }: Props) {
  const initialize = useBoolean(true);
  const reload = useBoolean(false);
  const form = useBoolean(false);
  const loading = useBoolean();
  const [edit, setEdit] = useState<ProductAmountConfigType | null>(null);

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<ProductAmountConfigType>({ defaultRowsPerPage: 10 });

  useEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      limit: rowsPerPage,
      productId: product.id,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
    };
    fetchData(params);
  }, [currentPage, rowsPerPage, reload.value]);

  useEffect(() => {
    if (!form.value) {
      setEdit(null);
    }
  }, [form.value]);

  const fetchData = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();

      const { data } = await axios.get<Pagination<ProductAmountConfigType>>(
        endpoints.productAmountConfig.main,
        {
          params: fParams({ ...params, productId: product.id }),
        }
      );

      // console.log({ data });

      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  const handleReload = useCallback(() => {
    onResetPage();
    reload.onToggle();
  }, []);

  const handleEdit = useCallback(async (amount: ProductAmountConfigType) => {
    setEdit(amount);
    form.onTrue();
  }, []);

  const handleDelete = useCallback(async (amount: ProductAmountConfigType) => {
    try {
      loading.onTrue();
      await axios.delete(endpoints.productAmountConfig.byId(amount.id));
      handleReload();
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  }, []);

  return (
    <>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6" gutterBottom>
            จำนวนทอง {product.name}
          </Typography>
          <Button
            onClick={form.onTrue}
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            สร้างรายการจำนวนทอง
          </Button>
        </Stack>

        <Card>
          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table sx={{ minWidth: 800 }}>
                <TableHeadCustom headLabel={tableHead} />

                <TableBody>
                  <TableLoading loading={initialize.value} rows={rowsPerPage}>
                    {tableData.map((row) => (
                      <ProductAmountTableRow
                        key={row.id}
                        row={row}
                        loading={loading.value}
                        onEdit={() => handleEdit(row)}
                        onDelete={() => handleDelete(row)}
                      />
                    ))}

                    <TableEmptyRows
                      show={!initialize.value}
                      height={76}
                      emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                    />

                    <TableNoData notFound={!tableData.length} />
                  </TableLoading>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={meta.totalResults}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </Card>
      </Stack>

      <ProductAmountFormDialog
        open={form.value}
        onClose={form.onFalse}
        handleReload={handleReload}
        product={product}
        productAmount={edit}
      />
    </>
  );
}
